import { h, render } from 'preact';
import { useState, useEffect, useReducer, useRef } from 'preact/hooks';
import { Utils, BeansLiquidEngine, riperStorage } from '@/base/helpers';
import { useTemplet, useRule, useActivity, useLeaderboard, useVIPTiers } from '@/liana/hooks';
import ActionModal from '@/liana/views/modals/ActionModal';
import Leaderboard from '../modals/Leaderboard';
import './ActivePage.css';

export default function ActivePage(props) {
  // props: { display, account, tabulator, Strings }
  const [selectedRule, setSelectedRule] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const timeoutRef = useRef(null);
  const templetNode = useRef(null);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const { display, tabulator, Strings, account, onLogin, onRegister } = props;
  const rules = useRule({ account, Strings, display });
  const [activityPendingList, activityList] = useActivity(account);
  const redeemableCollections = display.redemption_collection_items;
  const [isInstalled, currentShopper, allLeaderboardMembers] = useLeaderboard(account);
  const { cart } = riperStorage;
  const { tiers, shopperTier } = useVIPTiers(account, display);

  const { templet, templetError, templetLoading } = useTemplet({ ruleUid: 'rule:liana:display' });

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (tabulator.getActive() === 'rewards') window.Beans3.Liana.View.renderCart();
    });

    templetNode?.current && observer.observe(templetNode.current, { childList: true, attributes: true });

    return () => {
      observer.disconnect();
    };
  }, [templetNode.current]);

  const onTabChange = (tag) => {
    tabulator.onChange(tag);
    forceUpdate();
  };

  const openModal = (rule) => {
    setSelectedRule(rule);
    setIsModalOpen(true);
  };

  const closeModal = (delay = 0) => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsModalOpen(false);
    }, delay);
  };

  const handleRuleActionClick = (ruleStr) => {
    const rule = JSON.parse(ruleStr);
    if (
      rule.uid === Utils.LianaRulesUID.FRIEND_BEFORE_FIRST_PURCHASE ||
      rule.uid === Utils.LianaRulesUID.REFERRER_AFTER_FIRST_PURCHASE
    ) {
      return riperStorage.onAboutBamboo();
    }
    return Utils.handleRuleActionCTAClick(rule, openModal);
  };

  const handleJoinLeaderboard = () => {
    if (!account) {
      riperStorage.onLogin();
      return;
    }
    const container = document.createElement('div');
    container.id = 'liana-leaderboard-modal-container';
    const activePage = document.querySelector('.liana-page-active');
    activePage.appendChild(container);

    render(
      <Leaderboard
        account={riperStorage.account}
        onClose={() => {
          container.remove();
        }}
        isOpen
      />,
      container
    );
  };

  if (templetLoading) {
    return <p style={{ textAlign: 'center' }}>{Strings.loading?.loading_page}</p>;
  }

  if (templetError) {
    return (
      <p style={{ textAlign: 'center' }}>
        Something went wrong.{' '}
        <a href="#" className={{ cursor: 'pointer' }}>
          Please contact the Beans team.
        </a>
      </p>
    );
  }

  if (templet) {
    window.Beans3.Liana.Actions = {
      ...window.Beans3.Liana.Actions,
      onTabChange,
      tabulator,
      onLogin,
      onRegister,
      handleRuleActionClick,
      handleJoinLeaderboard,
    };

    const renderedTempletHTML = BeansLiquidEngine.parseAndRenderSync(templet.html, {
      display,
      tabulator,
      account,
      vip: { tiers, shopper: shopperTier },
      rules,
      Strings,
      isCartEmpty: !cart || (cart && cart.item_count === 0),
      activities: { pending: activityPendingList, past: activityList },
      leaderboard: {
        currentShopper,
        members: allLeaderboardMembers,
        isInstalled,
      },
      rewards: { products: [], collections: redeemableCollections },
      tabulatorMenu: tabulator.list(),
      activeTab: tabulator.getActive(),
      Params: templet.settings,
    });

    return (
      <div className="liana-page liana-page-active">
        {isModalOpen && (
          <ActionModal
            actionRule={selectedRule}
            isOpen={isModalOpen}
            onClose={closeModal}
            account={account}
            display={display}
          />
        )}
        {}
        <div ref={templetNode} dangerouslySetInnerHTML={{ __html: renderedTempletHTML }} />
      </div>
    );
  }
}
